import { useState, useContext, useEffect } from "react";
import formatCurrency from "../../../utils/formatCurrency";
import { AuthContext } from "../../../context/AuthContext";
import LoginForm from "../Auth/LoginForm";
import RegisterForm from "../Auth/RegisterForm";
import { useNavigate } from "react-router-dom";
import axios from "../../../axios";
import ErrorModal from "../../../admin/components/ErrorModal";
import handleApiError from "../../../utils/handleApiError";
import SuccessModal from "../../../admin/components/SuccessModal";

export default function ProductOrderForm({ product }) {
    const { user, setUser } = useContext(AuthContext);
    const [selected, setSelected] = useState({
        size: null,
        fabric: null,
        design: null,
        page: null,
    });
    const [order, setOrder] = useState(null);
    const [mode, setMode] = useState("login");
    const navigate = useNavigate();
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);

    // State untuk alamat dan ongkir
    const [provinces, setProvinces] = useState([]);
    const [cities, setCities] = useState([]);
    const [couriers, setCouriers] = useState([]);
    const [shippingAddress, setShippingAddress] = useState({
        province_id: "",
        city_id: "",
        address: "",
        postal_code: "",
        courier: "",
    });
    const [shippingCost, setShippingCost] = useState(0);
    const [shippingServices, setShippingServices] = useState([]);
    const [selectedService, setSelectedService] = useState("");

    // Load provinces saat component mount
    useEffect(() => {
        loadCouriers();
        loadProvinces();
    }, []);

    const loadCouriers = async () => {
        try {
            const res = await axios.get("/rajaongkir/couriers");
            setCouriers(res.data)
        } catch (err) {
            console.error("Error loading couriers:", err);
        }
    };



    const loadProvinces = async () => {
        try {
            const res = await axios.get("/rajaongkir/provinces");
            setProvinces(res.data.data);
        } catch (err) {
            console.error("Error loading provinces:", err);
        }
    };

    const loadCities = async (provinceId) => {
        try {
            const res = await axios.get(`/rajaongkir/cities?province=${provinceId}`);
            setCities(res.data.data);
        } catch (err) {
            console.error("Error loading cities:", err);
        }
    };

    const calculateShipping = async () => {
        setShippingCost(0);
        setSelectedService("");
        //
        
        if (!shippingAddress.city_id || !selected.size || !shippingAddress.courier) return;

        setLoading(true);
        try {
            const res = await axios.post("/rajaongkir/cost", {
                origin: "501", // ID kota asal (misal: Yogyakarta)
                destination: shippingAddress.city_id,
                weight: parseInt(product.weight) || 1000, // gram
                courier: shippingAddress.courier // bisa jne, pos, tiki
            });
            
            setShippingServices(res.data.data);
        } catch (err) {
            setError("Gagal menghitung ongkir: " + handleApiError(err));
        } finally {
            setLoading(false);
        }
    };

    // Hitung ongkir saat alamat berubah
    useEffect(() => {
        if (shippingAddress.city_id) {
            setShippingServices([]);
            calculateShipping();
        }
    }, [shippingAddress.city_id, selected.size, shippingAddress.courier]);

    const handleProvinceChange = (provinceId) => {
        setShippingAddress(prev => ({ ...prev, province_id: provinceId, city_id: "" }));
        setCities([]);
        setShippingServices([]);
        setSelectedService("");
        setShippingCost(0);
        if (provinceId) {
            loadCities(provinceId);
        }
    };

    const handleServiceChange = (service) => {
        console.log(service);
        setSelectedService(service);
        const selectedCost = shippingServices.find(s => s.service === service);
        setShippingCost(selectedCost ? selectedCost.cost : 0);
    };

    const get = (obj) => {
        return parseInt(obj?.pivot?.additional_price || obj?.price || 0);
    };

    const getSubtotal = () =>
        parseInt(product.category_price) + 
        parseInt(product.subcategory_price) + 
        get(selected.size) + 
        get(selected.fabric) + 
        get(selected.design) + 
        get(selected.page);

    const getTotal = () => getSubtotal() + shippingCost;

    const handleOrder = async () => {
        setLoading(true);
        
        try {
            const res = await axios.post("/orders", {
                product_id: product.id,
                size_id: selected.size?.id,
                cover_fabric_id: selected.fabric?.id,
                cover_design_id: selected.design?.id,
                pages: selected.page?.pages,
                shipping_address: shippingAddress,
                shipping_cost: shippingCost,
                shipping_service: selectedService,
            });
            
            setOrder(res.data);
            setSuccess("Data berhasil ditambahkan!");
            
        } catch (err) {
            setError(handleApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <h4 className="mb-20">Pesan Sekarang</h4>
            <ErrorModal error={error} onClose={() => setError("")} />
            <SuccessModal
                message={success}
                onClose={() => {
                    setSuccess("");
                    navigate("/checkout/" + order.id);
                }}
            />
            
            <div className="row gy-3">
                <SelectField
                    label="Ukuran"
                    options={product.sizes}
                    onChange={(id) => {
                        const found = product.sizes.find((s) => s.id === parseInt(id));
                        setSelected((prev) => ({ ...prev, size: found }));
                    }}
                    price={formatCurrency(get(selected.size))}
                />

                <SelectField
                    label="Cover Fabric"
                    options={product.cover_fabrics}
                    onChange={(id) => {
                        const found = product.cover_fabrics.find((f) => f.id === parseInt(id));
                        setSelected((prev) => ({ ...prev, fabric: found }));
                    }}
                    price={formatCurrency(get(selected.fabric))}
                />

                <SelectField
                    label="Cover Design"
                    options={product.cover_designs}
                    onChange={(id) => {
                        const found = product.cover_designs.find((d) => d.id === parseInt(id));
                        setSelected((prev) => ({ ...prev, design: found }));
                    }}
                    price={formatCurrency(get(selected.design))}
                />

                <SelectField
                    label="Jumlah Halaman"
                    options={product.page_numbers.map((p) => ({
                        id: p.pages,
                        name: `${p.pages} halaman`,
                        additional_price: p.price,
                    }))}
                    onChange={(pagess) => {
                        const found = product.page_numbers.find((p) => Number(p.pages) === Number(pagess));
                        setSelected((prev) => ({ ...prev, page: found }));
                    }}
                    price={formatCurrency(get(selected.page))}
                />
            </div>

            {/* Alamat Pengiriman */}
            <div className="mt-4">
                <h5 className="mb-3">Alamat Pengiriman</h5>
                <div className="row gy-3">
                    <div className="col-md-6">
                        <label className="form-label mb-2">Provinsi</label>
                        <select 
                            className="form-control"
                            value={shippingAddress.province_id}
                            onChange={(e) => handleProvinceChange(e.target.value)}
                        >
                            <option value="">-- Pilih Provinsi --</option>
                            {provinces.map((province) => (
                                <option key={province.id} value={province.id}>
                                    {province.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-6">
                        <label className="form-label mb-2">Kota/Kabupaten</label>
                        <select 
                            className="form-control"
                            value={shippingAddress.city_id}
                            onChange={(e) => setShippingAddress(prev => ({ ...prev, city_id: e.target.value }))}
                            disabled={!cities.length}
                        >
                            <option value="">-- Pilih Kota --</option>
                            {cities.map((city) => (
                                <option key={city.id} value={city.id}>
                                    {city.type} {city.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-8">
                        <label className="form-label mb-2">Alamat Lengkap</label>
                        <textarea 
                            className="form-control"
                            rows="3"
                            value={shippingAddress.address}
                            onChange={(e) => setShippingAddress(prev => ({ ...prev, address: e.target.value }))}
                            placeholder="Masukkan alamat lengkap..."
                        />
                    </div>

                    <div className="col-md-4">
                        <label className="form-label mb-2">Kode Pos</label>
                        <input 
                            type="text"
                            className="form-control"
                            value={shippingAddress.postal_code}
                            onChange={(e) => setShippingAddress(prev => ({ ...prev, postal_code: e.target.value }))}
                            placeholder="12345"
                        />
                    </div>

                    <div className="col-md-12">
                        <label className="form-label mb-2">Kurir</label>
                        <select 
                            className="form-control"
                            value={shippingAddress.courier}
                            onChange={(e) => setShippingAddress(prev => ({ ...prev, courier: e.target.value }))}
                        >
                            <option value="">-- Pilih Kurir --</option>
                            {couriers.map((courier) => (
                                <option key={courier.kode_kurir} value={courier.kode_kurir}>
                                    {courier.kurir}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Pilihan Ekspedisi */}
            {shippingServices.length > 0 && (
                <div className="mt-4">
                    <h5 className="mb-3">Pilih Layanan Pengiriman</h5>
                    <div className="row gy-2">
                        {shippingServices.map((service) => (
                            <div key={service.service} className="col-12">
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="shipping_service"
                                        value={service.service}
                                        checked={selectedService === service.service}
                                        onChange={(e) => handleServiceChange(e.target.value)}
                                    />
                                    <label className="form-check-label d-flex justify-content-between">
                                        <span>
                                            <strong>{service.service}</strong> - {service.description}
                                            <br />
                                            <small className="text-muted">Estimasi: {service.etd} hari</small>
                                        </span>
                                        <span className="fw-bold">
                                            Rp {service.cost.toLocaleString("id-ID")}
                                        </span>
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Total Harga */}
            <div className="mt-4">
                <div className="card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between mb-2">
                            <span>Subtotal Produk:</span>
                            <span>Rp {getSubtotal().toLocaleString("id-ID")}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                            <span>Ongkos Kirim:</span>
                            <span>Rp {shippingCost.toLocaleString("id-ID")}</span>
                        </div>
                        <hr />
                        <div className="d-flex justify-content-between">
                            <strong>Total:</strong>
                            <strong>Rp {getTotal().toLocaleString("id-ID")}</strong>
                        </div>
                    </div>
                </div>

                {!user || user.role !== 'user' ? (
                    mode === "login" ? (
                        <LoginForm onSwitchToRegister={() => setMode("register")} onSuccess={() => setMode(null)} />
                    ) : (
                        <RegisterForm onSwitchToLogin={() => setMode("login")} onSuccess={() => setMode(null)} />
                    )
                ) : (
                    <button
                        className="btn btn-main rounded-pill py-11 w-100 mt-16"
                        disabled={
                            !selected.size || 
                            !selected.fabric || 
                            !selected.design || 
                            !selected.page ||
                            !shippingAddress.province_id ||
                            !shippingAddress.city_id ||
                            !shippingAddress.address ||
                            !selectedService ||
                            loading
                        }
                        onClick={handleOrder}
                    >
                        {loading ? "Memproses..." : "Pesan Sekarang"}
                    </button>
                )}
            </div>
        </>
    );
}

function SelectField({ label, options, onChange, price = null }) {
    return (
        <div className="col-md-12">
            <label className="form-label mb-8 h6">{label} {price > 0 && '+' + price}</label>
            <select className="form-control py-11" onChange={(e) => onChange(e.target.value)}>
                <option value="">-- Pilih --</option>
                {options.map((item) => (
                    <option key={item.id} value={item.id}>
                        {item.name || item.id} (+Rp {parseInt(item.pivot?.additional_price || item.additional_price || 0).toLocaleString("id-ID")})
                    </option>
                ))}
            </select>
        </div>
    );
}
